/**
 * ElevationDims — single-equipment dimension overlay for a wall elevation.
 * Renders 4 colour-coded arrows: floor (amber up), ceiling (violet up),
 * left-neighbour (lime ←), right-neighbour (magenta →). Mirrors the 3D
 * DimensionOverlay palette so plan + elevation + 3D all read identically.
 *
 * Pure presentational — receives the selected item plus its wall context
 * and pre-computed screen-space helpers (sx, sy, wallLen) from WallPanel.
 */

export default function ElevationDims({ item, wallEquip, wall, wallLen, H, sx, sy }) {
  if (!item) return null;
  const p = item.position || {};
  const eOff = wall.axis === "x"
    ? p.x_in + (p.w_in || 0) / 2
    : p.y_in + (p.h_in || 0) / 2;
  const widthOnWall = wall.axis === "x"
    ? (p.w_in || item.length_in || 24)
    : (p.h_in || item.width_in  || 18);
  const heightOnWall = item.depth_in || 60;
  const baseY = Math.max(0, Number(item.mount_height_in) || 0);
  const left  = eOff - widthOnWall / 2;
  const right = eOff + widthOnWall / 2;

  // Nearest neighbours along the wall (only count ones that overlap
  // vertically with this item's footprint).
  let leftBlocker  = { off: 0,        tag: "wall" };
  let rightBlocker = { off: wallLen,  tag: "wall" };
  wallEquip.forEach((eq) => {
    if (eq.id === item.id) return;
    const ep = eq.position || {};
    const o  = wall.axis === "x"
      ? ep.x_in + (ep.w_in || 0) / 2
      : ep.y_in + (ep.h_in || 0) / 2;
    const ww = wall.axis === "x"
      ? (ep.w_in || eq.length_in || 24)
      : (ep.h_in || eq.width_in  || 18);
    const eL = o - ww / 2, eR = o + ww / 2;
    const ebaseY = Math.max(0, Number(eq.mount_height_in) || 0);
    const eheight = eq.depth_in || 60;
    const overlapsV = !(ebaseY > baseY + heightOnWall
                          || ebaseY + eheight < baseY);
    if (!overlapsV) return;
    if (eR <= left && eR > leftBlocker.off)   leftBlocker  = { off: eR, tag: eq.tag };
    if (eL >= right && eL < rightBlocker.off) rightBlocker = { off: eL, tag: eq.tag };
  });

  const dimensions = [
    { side: "floor",
      color: "#FFA66B",
      x1: sx(eOff), y1: sy(0),
      x2: sx(eOff), y2: sy(baseY),
      label: `↑ ${Math.round(baseY)}″`, target: "floor" },
    { side: "ceiling",
      color: "#B197FC",
      x1: sx(eOff), y1: sy(baseY + heightOnWall),
      x2: sx(eOff), y2: sy(H),
      label: `↑ ${Math.round(H - baseY - heightOnWall)}″`, target: "ceiling" },
    { side: "left",
      color: "#7CFFB2",
      x1: sx(leftBlocker.off), y1: sy(baseY + heightOnWall / 2),
      x2: sx(left),            y2: sy(baseY + heightOnWall / 2),
      label: `← ${Math.round(left - leftBlocker.off)}″`,
      target: leftBlocker.tag },
    { side: "right",
      color: "#FF66E0",
      x1: sx(right),            y1: sy(baseY + heightOnWall / 2),
      x2: sx(rightBlocker.off), y2: sy(baseY + heightOnWall / 2),
      label: `${Math.round(rightBlocker.off - right)}″ →`,
      target: rightBlocker.tag },
  ];

  return (
    <g pointerEvents="none" data-testid={`elev-dims-${item.tag}`}>
      {dimensions.map((d) => {
        if (Math.abs(d.x1 - d.x2) < 1 && Math.abs(d.y1 - d.y2) < 1) return null;
        const midX = (d.x1 + d.x2) / 2;
        const midY = (d.y1 + d.y2) / 2;
        return (
          <g key={d.side}>
            <line x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2}
                  stroke={d.color} strokeWidth="1.2" strokeDasharray="4 2" />
            <rect x={midX - 28} y={midY - 8} width="56" height="14"
                  fill="rgba(10,13,18,0.92)" stroke={d.color} strokeWidth="0.8" />
            <text x={midX} y={midY + 3} fill={d.color}
                  fontFamily="JetBrains Mono" fontSize="9" textAnchor="middle"
                  fontWeight="700">
              {d.label}
            </text>
            {d.target !== "wall" && d.target !== "floor" && d.target !== "ceiling" && (
              <text x={midX} y={midY + 13} fill={d.color}
                    fontFamily="JetBrains Mono" fontSize="7"
                    textAnchor="middle" opacity="0.85">
                {d.target}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
}
